import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RotateCcw, Upload, ImageIcon, FileText, AlertTriangle, Trash2, Sparkles } from "lucide-react";

export default function SettingsPage() {
  const { tenant, user, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wiping, setWiping] = useState(false);
  const [demoSummary, setDemoSummary] = useState(null);
  const [demoDeleting, setDemoDeleting] = useState(false);

  useEffect(() => {
    api.get("/tenant/demo-data/summary").then(r => setDemoSummary(r.data)).catch(() => setDemoSummary(null));
  }, []);

  useEffect(() => { if (tenant) setForm({ ...tenant }); }, [tenant]);

  if (!form) return <div>Lade…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/tenant", {
        name: form.name, logo_url: form.logo_url, letterhead_url: form.letterhead_url,
        primary_color: form.primary_color,
        secondary_color: form.secondary_color, accent_color: form.accent_color,
        address: form.address, vat_id: form.vat_id, default_vat_rate: Number(form.default_vat_rate),
        vat_method: form.vat_method, vat_period: form.vat_period,
        kleinunternehmer: form.kleinunternehmer, safety_reserve: Number(form.safety_reserve),
      });
      toast.success("Einstellungen gespeichert");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    setSaving(false);
  };

  const resetDemo = async () => {
    if (!window.confirm("Wirklich Demo-Daten zurücksetzen? Alle aktuellen Daten dieses Mandanten werden ersetzt.")) return;
    try {
      await api.post("/demo/reset");
      toast.success("Demo zurückgesetzt — bitte neu laden");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const isAdminOrOwner = user?.role === "owner" || user?.role === "admin";
  const isPlatformDemoOwner = user?.email === "owner@bullerbrothers.agency";

  const wipeData = async () => {
    if (wipeConfirm.trim().toUpperCase() !== "ALLES LOESCHEN") {
      toast.error('Bitte tippe „ALLES LOESCHEN“ zur Bestätigung.');
      return;
    }
    setWiping(true);
    try {
      const { data } = await api.post("/tenant/wipe-data", { confirmation: wipeConfirm });
      toast.success(`${data.total_deleted} Datensätze gelöscht. Dein Konto bleibt erhalten.`);
      setWipeOpen(false); setWipeConfirm("");
      setTimeout(() => window.location.assign("/"), 1200);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    setWiping(false);
  };

  const deleteDemoOnly = async () => {
    if (!window.confirm("Alle Demo-Datensätze aus der Sign-Up-Demo entfernen? Deine eigenen Daten bleiben unberührt.")) return;
    setDemoDeleting(true);
    try {
      const { data } = await api.post("/tenant/demo-data/delete");
      toast.success(`${data.total_deleted} Demo-Datensätze entfernt.`);
      setDemoSummary({ has_demo_data: false, counts: {}, total: 0 });
      setTimeout(() => window.location.assign("/"), 1200);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    setDemoDeleting(false);
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Einstellungen</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Mandant & Branding</h1>
      </div>

      <Section title="Firma">
        <Field label="Firmenname"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" /></Field>
        <Field label="Adresse"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
        <Field label="USt-ID"><Input value={form.vat_id || ""} onChange={(e) => setForm({ ...form, vat_id: e.target.value })} /></Field>
      </Section>

      <Section title="White-Label-Farben">
        <div className="grid grid-cols-3 gap-4">
          <Field label="Primärfarbe"><Input type="color" value={form.primary_color || "#0F172A"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} data-testid="settings-primary-color" /></Field>
          <Field label="Sekundärfarbe"><Input type="color" value={form.secondary_color || "#F1F5F9"} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} /></Field>
          <Field label="Akzentfarbe"><Input type="color" value={form.accent_color || "#22C55E"} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} /></Field>
        </div>
      </Section>

      <Section title="Logo & Briefkopf">
        <BrandingUploads form={form} onChange={(updates) => setForm(f => ({ ...f, ...updates }))} />
      </Section>

      <Section title="Umsatzsteuer">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Standard-USt-Satz %"><Input type="number" value={form.default_vat_rate} onChange={(e) => setForm({ ...form, default_vat_rate: e.target.value })} /></Field>
          <Field label="Methode">
            <Select value={form.vat_method} onValueChange={(v) => setForm({ ...form, vat_method: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ist">Ist-Versteuerung</SelectItem>
                <SelectItem value="soll">Soll-Versteuerung</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="UStVA-Zeitraum">
            <Select value={form.vat_period} onValueChange={(v) => setForm({ ...form, vat_period: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monatlich</SelectItem>
                <SelectItem value="quarterly">Quartalsweise</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex items-center justify-between border rounded-lg px-4 py-2">
            <Label className="m-0">Kleinunternehmer (§19 UStG)</Label>
            <Switch checked={!!form.kleinunternehmer} onCheckedChange={(v) => setForm({ ...form, kleinunternehmer: v })} />
          </div>
        </div>
      </Section>

      <Section title="Sicherheitsrücklage">
        <Field label="Pufferbetrag der immer als Rücklage gilt (€)">
          <Input type="number" step="100" value={form.safety_reserve} onChange={(e) => setForm({ ...form, safety_reserve: e.target.value })} data-testid="settings-safety" />
        </Field>
      </Section>

      <Section title="E-Mail-Versand (für Mahnungen & Einladungen)">
        <EmailSettingsBlock />
      </Section>

      <div className="flex items-center justify-between pt-2">
        <div />
        <Button onClick={save} disabled={saving} data-testid="settings-save">{saving ? "Speichert…" : "Änderungen speichern"}</Button>
      </div>

      {demoSummary?.has_demo_data && (
        <div className="rounded-xl border-2 border-blue-200 bg-blue-50/40 p-6" data-testid="demo-data-card">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-blue-700 font-semibold">Demo-Daten</div>
              <div className="font-display text-lg font-semibold text-neutral-900 mt-1">
                Beispieldaten aus dem Sign-Up entfernen
              </div>
              <p className="text-sm text-neutral-700 mt-1 max-w-xl">
                Beim Anlegen deines Kontos hast du „Mit Demo-Daten starten" aktiviert.
                Aktuell sind noch <strong>{demoSummary.total} Beispiel-Einträge</strong> vorhanden
                (Geschäftskonto, Beispiel-Kunden, Website-Relaunch-Projekt, Muster-Rechnungen &amp; Notion/Miete-Fixkosten).
                Sobald du deine echten Daten pflegst, kannst du sie entfernen — <strong>deine eigenen Datensätze bleiben unangetastet</strong>.
              </p>
              <div className="mt-4">
                <Button
                  variant="outline"
                  className="border-blue-300 text-blue-800 hover:bg-blue-100"
                  onClick={deleteDemoOnly}
                  disabled={demoDeleting}
                  data-testid="delete-demo-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> {demoDeleting ? "Entferne…" : "Demo-Daten entfernen"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAdminOrOwner && (
        <div className="rounded-xl border-2 border-red-200 bg-red-50/40 p-6" data-testid="danger-zone">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-red-700 font-semibold">Gefahren-Zone</div>
              <div className="font-display text-lg font-semibold text-neutral-900 mt-1">Alle Daten löschen</div>
              <p className="text-sm text-neutral-700 mt-1 max-w-xl">
                Löscht alle Konten, Kunden, Projekte, Rechnungen, Fixkosten und Rücklagen deiner Agentur. Team-Mitglieder, dein Login und die Firmen­einstellungen (Farben, Logo) <strong>bleiben erhalten</strong>. Perfekt, um nach der Demo-Phase mit deinen echten Daten zu starten.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-100" onClick={() => setWipeOpen(true)} data-testid="wipe-data-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Alle Daten dieses Mandanten löschen
            </Button>
            {isPlatformDemoOwner && (
              <Button variant="outline" onClick={resetDemo} data-testid="reset-demo-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Buller-Demo neu seeden
              </Button>
            )}
          </div>
        </div>
      )}

      <Dialog open={wipeOpen} onOpenChange={(v) => { setWipeOpen(v); if (!v) setWipeConfirm(""); }}>
        <DialogContent className="max-w-lg" data-testid="wipe-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Alle Daten löschen — wirklich?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Diese Aktion kann <strong>nicht rückgängig</strong> gemacht werden.</p>
            <div className="rounded-lg bg-red-50 border border-red-200 p-3">
              <div className="font-medium text-red-800 mb-1">Wird gelöscht:</div>
              <div className="text-red-700 text-xs">Konten · Kunden · Projekte · Ein-/Ausgangsrechnungen · Zahlungen · Fixkosten · Rücklagen · Belege · Warnungen · Audit-Log</div>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
              <div className="font-medium text-emerald-800 mb-1">Bleibt bestehen:</div>
              <div className="text-emerald-700 text-xs">Dein Login · Team-Mitglieder · Mandantenname, Adresse, USt-ID · Logo · Farben · E-Mail-Einstellungen</div>
            </div>
            <div>
              <Label className="text-xs">Zur Bestätigung tippe <span className="font-mono bg-neutral-100 px-1.5 py-0.5 rounded">ALLES LOESCHEN</span></Label>
              <Input value={wipeConfirm} onChange={(e) => setWipeConfirm(e.target.value)} placeholder="ALLES LOESCHEN" autoFocus data-testid="wipe-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setWipeOpen(false); setWipeConfirm(""); }}>Abbrechen</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={wiping || wipeConfirm.trim().toUpperCase() !== "ALLES LOESCHEN"}
              onClick={wipeData}
              data-testid="wipe-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> {wiping ? "Lösche…" : "Endgültig löschen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (<div><Label className="text-xs text-neutral-500 mb-1.5 block">{label}</Label>{children}</div>);
}

function EmailSettingsBlock() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    provider: "smtp", from_email: "", from_name: "",
    smtp_host: "", smtp_port: 587, smtp_username: "", smtp_password: "", smtp_use_tls: true,
    resend_api_key: "",
  });
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/email-settings").then(r => {
      setSettings(r.data);
      if (r.data) setForm(f => ({ ...f, ...r.data, smtp_password: "", resend_api_key: "" }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/email-settings", form);
      toast.success("E-Mail-Einstellungen gespeichert");
      const r = await api.get("/email-settings");
      setSettings(r.data);
      setForm(f => ({ ...f, ...r.data, smtp_password: "", resend_api_key: "" }));
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    setSaving(false);
  };

  const test = async () => {
    if (!testTo) { toast.error("Empfänger-Adresse eingeben"); return; }
    try {
      await api.post(`/email-settings/test?to=${encodeURIComponent(testTo)}`);
      toast.success(`Test-Mail an ${testTo} versendet`);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Provider">
          <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} data-testid="email-provider">
            <option value="smtp">Eigener SMTP-Server (z.B. Strato, IONOS, Gmail)</option>
            <option value="resend">Resend (API)</option>
          </select>
        </Field>
        <Field label="Absender-Name"><Input value={form.from_name || ""} onChange={(e) => setForm({ ...form, from_name: e.target.value })} placeholder="z.B. Buller Brothers Agency" /></Field>
        <Field label="Absender-Adresse"><Input type="email" value={form.from_email || ""} onChange={(e) => setForm({ ...form, from_email: e.target.value })} data-testid="email-from" /></Field>
      </div>

      {form.provider === "smtp" ? (
        <div className="grid grid-cols-2 gap-4 border-t pt-4">
          <Field label="SMTP-Host"><Input value={form.smtp_host || ""} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} placeholder="smtp.strato.de" data-testid="email-smtp-host" /></Field>
          <Field label="Port"><Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} /></Field>
          <Field label="Benutzername"><Input value={form.smtp_username || ""} onChange={(e) => setForm({ ...form, smtp_username: e.target.value })} /></Field>
          <Field label="Passwort">
            <Input type="password" value={form.smtp_password || ""} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} placeholder={settings?.smtp_password_set ? "Leer = unverändert" : ""} data-testid="email-smtp-password" />
          </Field>
          <div className="col-span-2 flex items-center justify-between border rounded-lg px-4 py-2">
            <Label className="m-0">STARTTLS verwenden (Port 587 empfohlen)</Label>
            <Switch checked={!!form.smtp_use_tls} onCheckedChange={(v) => setForm({ ...form, smtp_use_tls: v })} />
          </div>
        </div>
      ) : (
        <div className="border-t pt-4">
          <Field label="Resend API-Key">
            <Input type="password" value={form.resend_api_key || ""} onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })} placeholder={settings?.resend_key_set ? "Leer = unverändert" : "re_..."} data-testid="email-resend-key" />
          </Field>
          <p className="text-xs text-neutral-500 mt-2">API-Key unter <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="underline">resend.com/api-keys</a> erstellen. Absender-Domain muss verifiziert sein (DKIM).</p>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2 border-t">
        <Button onClick={save} disabled={saving} data-testid="email-save">{saving ? "…" : "Speichern"}</Button>
        <Input className="max-w-xs" type="email" placeholder="test@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="email-test-to" />
        <Button variant="outline" onClick={test} data-testid="email-test-send">Test-Mail senden</Button>
      </div>
    </div>
  );
}

function BrandingUploads({ form, onChange }) {
  const logoRef = useRef(null);
  const lhRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file, purpose) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", purpose);
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (purpose === "logo") onChange({ logo_url: data.storage_path });
      if (purpose === "letterhead") onChange({ letterhead_url: data.storage_path });
      toast.success(purpose === "logo" ? "Logo hochgeladen" : "Briefkopf hochgeladen");
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    setBusy(false);
  };

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="rounded-lg border border-neutral-200 p-4">
        <div className="flex items-center gap-2 text-sm font-medium mb-2"><ImageIcon className="h-4 w-4" />Logo</div>
        <p className="text-xs text-neutral-500 mb-3">PNG/JPG/WEBP — wird in Sidebar und PDF-Header verwendet.</p>
        {form.logo_url ? (
          <div className="text-xs text-neutral-600 mb-2 break-all">📎 {form.logo_url.split("/").pop()}</div>
        ) : <div className="text-xs text-neutral-500 mb-2">Noch kein Logo</div>}
        <input ref={logoRef} type="file" hidden accept=".png,.jpg,.jpeg,.webp" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "logo")} data-testid="logo-file-input" />
        <Button size="sm" variant="outline" onClick={() => logoRef.current?.click()} disabled={busy} data-testid="logo-upload-btn"><Upload className="h-3.5 w-3.5 mr-2" />Logo hochladen</Button>
      </div>
      <div className="rounded-lg border border-neutral-200 p-4">
        <div className="flex items-center gap-2 text-sm font-medium mb-2"><FileText className="h-4 w-4" />Briefkopf</div>
        <p className="text-xs text-neutral-500 mb-3">Bild (PNG/JPG) — wird als Briefkopf-Banner in allen PDF-Exporten verwendet.</p>
        {form.letterhead_url ? (
          <div className="text-xs text-neutral-600 mb-2 break-all">📎 {form.letterhead_url.split("/").pop()}</div>
        ) : <div className="text-xs text-neutral-500 mb-2">Kein Briefkopf gesetzt</div>}
        <input ref={lhRef} type="file" hidden accept=".png,.jpg,.jpeg" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "letterhead")} data-testid="letterhead-file-input" />
        <Button size="sm" variant="outline" onClick={() => lhRef.current?.click()} disabled={busy} data-testid="letterhead-upload-btn"><Upload className="h-3.5 w-3.5 mr-2" />Briefkopf hochladen</Button>
      </div>
    </div>
  );
}
